/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **One control that yields either an authored list or a reference to one** (`#1280`).
 *
 * Reported by Todd 2026-09-08 against `E2b`: *"I don't see the path typedown on the RHS."* Every
 * other condition operand had one; the list operator's did not, and the model, the reader and the
 * engine all resolve a path that holds a list — so the capability existed and could not be authored.
 *
 * ## Why this is a branch and not a second control
 *
 * The first write-up called it *not cheap*, on the reasoning that `ListField` owns a draft string
 * because parsing per keystroke deletes the separator as it is typed (`#1199`), so a control
 * yielding two shapes would be a new one. Todd: *"we fixed the 'deletes the separator' — what makes
 * this hard?"* Nothing, and the claim was wrong: draft ownership is exactly what `#1199` **solved**,
 * and it is reused here rather than re-solved. What was left is a second **reading** of the same
 * draft, which is one predicate.
 *
 * ## The predicate is the value line's, not a new one
 *
 * `readsAsPath` — canonicalise, then require a **declared root** — is what every other operand uses,
 * and using a different rule here is how one vocabulary comes to mean two things (*one matcher per
 * vocabulary*). It is deliberately not *"contains a dot"*: `roku.tv` and `1.5` are literals, and a
 * draft with whitespace (`kids, family`) is never a path.
 *
 * ## What a path resolving to something else does
 *
 * Todd raised it and it settles itself: *"what if it's a complex object? But that's true everywhere
 * a path is referenced."* Right — an object on either side of a comparison is the engine's existing
 * question, answered the same way here as anywhere, and it is not a reason to withhold the reference.
 * A path holding a **single value** is simply a comparison against one member, which `isAnyOf`
 * already treats as a one-member list.
 */

import React from 'react';
import { MappingAutocompleteInput, type MappingSuggestion } from './MappingAutocompleteInput';
import { listToDraft, parseListDraft, shouldResyncListDraft } from './ListField.helpers';
import { readsAsPath } from '../editors/value-line';
import { ReadOut } from '../editors/ValueLine';
import { HStack, Box } from '@chakra-ui/react';

export type ListOrPath = { kind: 'list'; members: string[] } | { kind: 'path'; path: string };

export interface ListOrPathFieldProps {
  /** The authored list, when one is what is stored. */
  members: readonly (string | number | boolean)[] | undefined;
  /** The referenced path, when that is what is stored. Exactly one of the two is set. */
  path: string | undefined;
  onChange: (next: ListOrPath) => void;
  /** Path autocomplete for this surface — **required**, `null` to decline (`#1134`). */
  getSuggestions: ((query: string) => MappingSuggestion[] | Promise<MappingSuggestion[]>) | null;
  placeholder?: string;
}

export const ListOrPathField: React.FC<ListOrPathFieldProps> = ({
  members,
  path,
  onChange,
  getSuggestions,
  placeholder,
}) => {
  const stored = path ?? listToDraft(members);
  const [draft, setDraft] = React.useState<string>(stored);

  React.useEffect(() => {
    setDraft((current) => {
      // A path re-syncs on a plain disagreement; a list defers to `#1199`'s rule, which is that
      // `"kids, "` already *means* `['kids']` and re-rendering it would delete the comma being typed.
      if (path !== undefined) return current === path ? current : path;
      return shouldResyncListDraft(current, members) ? listToDraft(members) : current;
    });
  }, [members, path]);

  const asPath = readsAsPath(draft);

  return (
    <HStack gap={0} align="stretch">
      {/* The same read-out every other operand carries, from the same component — two of them would
          let one vocabulary describe itself two ways, which is what `X24` made a read-out to end. */}
      <ReadOut
        kind={draft.trim() === '' ? 'empty' : asPath ? 'path' : 'literal'}
        label={draft.trim() === '' ? '' : asPath ? 'path' : 'list'}
        title={asPath ? 'A path that resolves to a list — each member is compared' : undefined}
      />
      <Box flex="1" minW={0}>
        <MappingAutocompleteInput
          value={draft}
          onChange={(next) => {
            setDraft(next);
            onChange(
              readsAsPath(next) ? { kind: 'path', path: next.trim() } : { kind: 'list', members: parseListDraft(next) },
            );
          }}
          placeholder={placeholder}
          getSuggestions={getSuggestions ?? undefined}
          size="sm"
          bg="bg.card"
        />
      </Box>
    </HStack>
  );
};
